import logo from './logo.svg';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import OpportunityAndFilters from './components/OpportunityAndFilters';
import '@maersk-global/fonts/maeu/fonts.css';
import Login from './components/Login';
import { useMsal } from '@azure/msal-react';
import { useIsAuthenticated } from '@azure/msal-react';
import HomeGraphMetrics from './components/HomeGraphMetrics';
import '@maersk-global/mds-design-tokens/maersk/light/css/design-tokens-px.css';
import '@maersk-global/mds-foundations/css/foundations.css';
import ProfilePage from './components/ProfilePage';
import MaintenancePage from './components/MaintenancePage';
import { apiUrl, msalConfig } from './authconfig';
import UserAccess from './components/UserAccess';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import AccessCheckLoader from './components/AcessCheckLoader';
import AccessRequested from './components/AccessRequested';
import AccessDenied from './components/AccessDenied';
import RequestAccessLoader from './components/RequestAccessLoader';
import apiClient, { initApiClient } from './api/apiClient';

const SESSION_ACCESS_PHASE_KEY = 'dealtoreal_user_access_phase';
const SESSION_PENDING_COUNT_KEY = 'dealtoreal_user_access_pending_count';
const ACCESS_API_BASE = `${String(apiUrl || '').replace(/\/$/, '')}/api/user-access`;

function formatDateDisplay(value) {
  if (!value) return '';
  try {
    // Ensure consistent parsing by treating ambiguous strings as UTC
    const dateStr = String(value).trim();
    const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00Z');
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString();
  } catch (e) {
    return '';
  }
}
const isDevEnvironment = () => {
    // Check the actual values being used (including fallbacks from authconfig.js)
  const currentApiUrl = apiUrl;
  const currentRedirectUri = msalConfig.auth.redirectUri;
  console.log("Current API URL in Maintenance:", currentApiUrl);
  console.log("Current Redirect URI in Maintenance:", currentRedirectUri);

  // For local testing, check for localhost:3000
  // For dev deployment, check for 'ionis-dev'
  return (currentApiUrl && (currentApiUrl.includes('ionis-dev') || currentApiUrl.includes('ionis-dev'))) ||
          (currentRedirectUri && (currentRedirectUri.includes('ionis-dev') || currentRedirectUri.includes('ionis-dev')));
};
function ProtectedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeGraphMetrics />} />
      <Route path="/opportunities" element={<OpportunityAndFilters />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/user-access" element={<UserAccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
function App() {
    const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const hasCheckedThisSession = useRef(false);

  useLayoutEffect(() => {
    if (isAuthenticated && accounts?.[0]) {
      initApiClient(instance, accounts[0]);
    }
  }, [isAuthenticated, instance, accounts]);
  const [submitError, setSubmitError] = useState('');
  const [deniedMeta, setDeniedMeta] = useState({ reason: '', date: '', allowRequestAccess: true });

  const [accessPhase, setAccessPhase] = useState(() => {
    if (typeof window === 'undefined') return null;
    return sessionStorage.getItem(SESSION_ACCESS_PHASE_KEY);
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setAccessPhase(null);
      hasCheckedThisSession.current = false;
      sessionStorage.removeItem(SESSION_ACCESS_PHASE_KEY);
      setDeniedMeta({ reason: '', date: '', allowRequestAccess: true });
      setSubmitError('');
      return;
    }

    if (inProgress !== InteractionStatus.None) return;
    if (!accounts?.length) return;

    if (hasCheckedThisSession.current) return;

    hasCheckedThisSession.current = true;
    initApiClient(instance, accounts[0]);
    setAccessPhase('checking');
    setSubmitError('');

    const userPrincipalName =
      accounts[0]?.idTokenClaims?.email ||
      accounts[0]?.username ||
      '';

    const checkAccess = async () => {
      try {
        const { data } = await apiClient.get(`${ACCESS_API_BASE}/check-user`, {
          params: { userPrincipalName },
        });

        let phase = 'request-access';
        //Redirect to prod URL if user doesn't have access in dev environment
        if (isDevEnvironment() && !data?.exists) {
          window.location.href = 'https://ionis.maersk-digital.net/opportunities';
          return;
        } 
        else if (data?.exists) {
          phase = 'granted';
        } else if (data?.hasPendingRequest) {
          phase = 'requested';
        } else if (data?.hasRejectedRequest) {
          phase = 'denied';
          setDeniedMeta({
            reason: data?.rejectionReason || 'Your request does not meet current access policy requirements.',
            date: formatDateDisplay(data?.rejectionDate),
            allowRequestAccess: true,
          });
        }

        sessionStorage.setItem(SESSION_ACCESS_PHASE_KEY, phase);
        if (phase === 'granted' && !sessionStorage.getItem(SESSION_PENDING_COUNT_KEY)) {
          sessionStorage.setItem(SESSION_PENDING_COUNT_KEY, '0');
        }
        setAccessPhase(phase);
      } catch (error) {
        setAccessPhase('denied');
        setDeniedMeta({
          reason: 'Unable to verify access at this time. Please contact support.',
          date: formatDateDisplay(new Date().toISOString()),
          allowRequestAccess: true,
        });
      }
    };

    checkAccess();
  }, [accounts, instance, inProgress, isAuthenticated]);

  if (!isAuthenticated) return <Login />;

  if (accessPhase === 'checking') return <AccessCheckLoader />;

  if (accessPhase === 'requested') return <AccessRequested />;

  if (accessPhase === 'request-access') {
    return (
      <RequestAccessLoader
        submitError={submitError}
        onRequestAccess={async (justification) => {
          setSubmitError('');
          try {
            const userPrincipalName =
              accounts?.[0]?.idTokenClaims?.email ||
              accounts?.[0]?.username ||
              '';
            const displayName = accounts?.[0]?.name || '';

            await apiClient.post(`${ACCESS_API_BASE}/request`, {
              userPrincipalName,
              displayName,
              objectType: 'User',
              userType: 'Member',
              isUser: true,
              isGroup: false,
              isGuest: false,
              comments: justification,
            });

            sessionStorage.setItem(SESSION_ACCESS_PHASE_KEY, 'requested');
            setAccessPhase('requested');
          } catch (error) {
            const message =
              error?.response?.data?.error ||
              error?.response?.data?.message ||
              error?.message ||
              'Failed to submit access request.';
            setSubmitError(message);
          }
        }}
      />
    );
  }

  if (accessPhase === 'denied') {
    return (
      <AccessDenied
        deniedReason={deniedMeta.reason}
        deniedDate={deniedMeta.date}
        onRequestAccess={deniedMeta.allowRequestAccess ? () => {
          setSubmitError('');
          setAccessPhase('request-access');
          sessionStorage.setItem(SESSION_ACCESS_PHASE_KEY, 'request-access');
        } : undefined}
      />
    );
  }

  if (accessPhase === 'granted') {
    return (
      <div>
        {isAuthenticated ?
          (
            <Router>
              <Header />
              <ProtectedRoutes />
              <Footer />
            </Router>
          )
          : <Login />
        }
      </div>
    );
  }

  return <AccessCheckLoader />;
}
export default App;
